import React, { useEffect } from 'react';
import { X } from 'lucide-react';

/**
 * Reusable Modal Component
 * 
 * Professional modal dialog with backdrop, animations, and keyboard handling.
 * Used for forms, confirmations, and detailed views throughout the admin panel.
 */
const Modal = ({
  isOpen = false,
  onClose = () => {},
  title = '',
  children,
  footer = null,
  size = 'md',
  closeOnBackdrop = true,
  className = '',
}) => {
  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md mx-4 md:mx-auto',
    md: 'max-w-lg mx-4 md:mx-auto',
    lg: 'max-w-2xl mx-4 md:mx-auto',
    xl: 'max-w-4xl mx-4 md:mx-auto',
    full: 'max-w-7xl mx-4 md:mx-auto',
  };

  const handleBackdropClick = (event) => {
    if (closeOnBackdrop && event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden" onClick={handleBackdropClick}>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-secondary-600 bg-opacity-75 transition-opacity pointer-events-none" />

      {/* Modal container */}
      <div className="flex items-end md:items-center justify-center h-full md:p-4 pointer-events-none">
        <div
          className={`
            relative bg-white rounded-t-lg md:rounded-lg shadow-xl transform transition-all w-full
            max-h-[95vh] md:max-h-[90vh] flex flex-col pointer-events-auto
            ${sizeClasses[size]} ${className}
          `}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 md:p-6 border-b border-secondary-200 bg-white sticky top-0 z-10">
            <h3 className="text-lg font-semibold text-secondary-900 truncate pr-4">
              {title}
            </h3>
            <button
              onClick={onClose}
              className="text-secondary-400 hover:text-secondary-600 transition-colors flex-shrink-0 p-1"
            >
              <X className="w-5 h-5 md:w-6 md:h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 md:p-6 overflow-y-auto flex-1 min-h-0">
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div className="flex justify-end space-x-3 px-4 md:px-6 py-4 bg-secondary-50 rounded-b-lg md:rounded-b-lg border-t border-secondary-200 flex-shrink-0">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Confirmation Modal Component
 * 
 * Specialized modal for confirmation dialogs with consistent styling.
 */
export const ConfirmModal = ({
  isOpen = false,
  onClose = () => {},
  onConfirm = () => {},
  title = 'Confirm Action',
  message = 'Are you sure you want to perform this action?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  loading = false,
}) => {
  const handleConfirm = async () => {
    await onConfirm();
    onClose();
  };

  const variantClasses = {
    danger: 'btn-danger',
    primary: 'btn-primary',
    secondary: 'btn-secondary',
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <button
            onClick={onClose}
            disabled={loading}
            className="btn btn-secondary"
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className={`btn ${variantClasses[variant]} ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {loading ? 'Processing...' : confirmText}
          </button>
        </>
      }
    >
      <p className="text-secondary-600">{message}</p>
    </Modal>
  );
};

/**
 * Form Modal Component
 * 
 * Specialized modal for forms with consistent form handling.
 */
export const FormModal = ({
  isOpen = false,
  onClose = () => {},
  onSubmit = () => {},
  title = '',
  children,
  submitText = 'Save',
  cancelText = 'Cancel',
  loading = false,
  size = 'md',
}) => {
  const handleSubmit = async (event) => {
    event.preventDefault();
    await onSubmit(event);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size={size}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="btn btn-secondary"
          >
            {cancelText}
          </button>
          <button
            type="submit"
            form="modal-form"
            disabled={loading}
            className={`btn btn-primary ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {loading ? 'Saving...' : submitText}
          </button>
        </>
      }
    >
      <form id="modal-form" onSubmit={handleSubmit}>
        {children}
      </form>
    </Modal>
  );
};

export default Modal;